import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { UploadSimple, Trash, ArrowsClockwise, Info, ArrowClockwise, CheckCircle, XCircle, CircleNotch, DownloadSimple, Prohibit } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../api';

const Upload = () => {
  const [statementName, setStatementName] = useState('');
  const [file, setFile] = useState(null);
  const [password, setPassword] = useState('');
  const [uploading, setUploading] = useState(false);
  const [statements, setStatements] = useState([]);
  const [settings, setSettings] = useState(null);
  const [reloadingAll, setReloadingAll] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  
  // Batch upload state
  const [batchFiles, setBatchFiles] = useState([]);
  const [batchUploading, setBatchUploading] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0, currentFile: '' });
  const [batchFailedFiles, setBatchFailedFiles] = useState([]);
  const [pendingBatchFiles, setPendingBatchFiles] = useState([]);
  
  // Reload state - track individual statement status
  // status: 'pending' | 'loading' | 'success' | 'failed'
  const [statementReloadStatus, setStatementReloadStatus] = useState({});
  const [hasReloadFailures, setHasReloadFailures] = useState(false);
  
  // Constants
  const MAX_BATCH_LIMIT = 25;

  useEffect(() => {
    fetchStatements();
    fetchSettings();
  }, []);

  const fetchStatements = async () => {
    try {
      const response = await api.get('/statements');
      setStatements(response.data);
    } catch (error) {
      toast.error('Failed to load statements');
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load settings');
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
    } else {
      toast.error('Please select a PDF file');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!statementName || !file || !password) {
      toast.error('Please fill all fields');
      return;
    }

    // Check statement limit before upload
    const maxStatements = settings?.max_statements || 75;
    if (statements.length >= maxStatements) {
      toast.error(`Maximum ${maxStatements} statements reached. Please delete older statements before uploading new ones.`);
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('statement_name', statementName);
    formData.append('password', password);

    try {
      const response = await api.post('/statements/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      if (response.data.duplicate_replaced) {
        toast.warning(`Duplicate replaced: ${statementName} (${response.data.statement_month}/${response.data.statement_year}) - ${response.data.transactions_extracted} transactions extracted`);
      } else {
        toast.success(`Statement uploaded! ${response.data.transactions_extracted} transactions extracted.`);
      }
      
      // Reset form
      setStatementName('');
      setFile(null);
      setPassword('');
      document.getElementById('file-upload').value = '';
      fetchStatements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload statement');
    } finally {
      setUploading(false);
    }
  };

  const handleBatchFileSelect = (e) => {
    const files = Array.from(e.target.files).filter(f => f.type === 'application/pdf');
    if (files.length === 0) {
      toast.error('Please select PDF files');
      return;
    }
    setBatchFiles(files);
  };

  const handleBatchUpload = async (retryFailed = false) => {
    const filesToProcess = retryFailed ? pendingBatchFiles : batchFiles;
    
    if (filesToProcess.length === 0) {
      toast.error('Select files to upload');
      return;
    }

    // Check max batch limit
    if (filesToProcess.length > MAX_BATCH_LIMIT) {
      toast.error(`Maximum ${MAX_BATCH_LIMIT} files allowed per batch upload. Please select fewer files.`);
      return;
    }

    // Check statement limit before batch upload
    const maxStatements = settings?.max_statements || 75;
    if (statements.length >= maxStatements) {
      toast.error(`Maximum ${maxStatements} statements reached. Please delete older statements before uploading new ones.`);
      return;
    }

    // Validate all files have password in filename
    const invalidFiles = filesToProcess.filter(f => !f.name.includes('_'));
    if (invalidFiles.length > 0) {
      toast.error(`${invalidFiles.length} file(s) missing password in filename. Use format: cardname_password.pdf`);
      return;
    }

    setBatchUploading(true);
    setBatchFailedFiles([]);
    setPendingBatchFiles([]);
    
    const totalFiles = filesToProcess.length;
    setBatchProgress({ current: 0, total: totalFiles, currentFile: '' });
    
    let successCount = 0;
    let duplicateCount = 0;
    const failedFilesList = [];
    const remainingFiles = [];

    for (let i = 0; i < filesToProcess.length; i++) {
      const file = filesToProcess[i];
      
      // Update progress with current file name BEFORE starting upload
      setBatchProgress({ current: i, total: totalFiles, currentFile: file.name });
      
      // Small delay to ensure UI updates
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // Check if limit reached during batch upload
      const currentCount = statements.length + successCount - duplicateCount;
      if (currentCount >= maxStatements) {
        toast.error(`Limit of ${maxStatements} statements reached. Remaining files skipped.`);
        remainingFiles.push(...filesToProcess.slice(i));
        break;
      }
      
      // Extract card name and password from filename (format: cardname_password.pdf)
      const filenameWithoutExt = file.name.replace('.pdf', '').replace('.PDF', '');
      const lastUnderscoreIndex = filenameWithoutExt.lastIndexOf('_');
      
      if (lastUnderscoreIndex === -1) {
        failedFilesList.push(file);
        continue;
      }
      
      const cardName = filenameWithoutExt.substring(0, lastUnderscoreIndex);
      const password = filenameWithoutExt.substring(lastUnderscoreIndex + 1);
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('statement_name', cardName);
      formData.append('password', password);

      try {
        const response = await api.post('/statements/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000
        });
        successCount++;
        
        // Update progress after successful upload
        setBatchProgress({ current: i + 1, total: totalFiles, currentFile: '' });
        
        if (response.data.duplicate_replaced) {
          duplicateCount++;
        }
      } catch (error) {
        failedFilesList.push(file);
        console.error(`Failed to upload ${file.name}:`, error);
        
        const errorMsg = error.response?.data?.detail || error.response?.data?.error || 'Upload failed';
        if (errorMsg.includes('Budget')) {
          toast.error(errorMsg);
          // Add remaining files to failed list for retry
          remainingFiles.push(...filesToProcess.slice(i + 1));
          break;
        }
      }
    }

    setBatchUploading(false);
    
    // Store failed files for retry
    const allFailedFiles = [...failedFilesList, ...remainingFiles];
    setBatchFailedFiles(allFailedFiles);
    setPendingBatchFiles(allFailedFiles);
    
    if (allFailedFiles.length === 0) {
      // All succeeded - clear everything
      setBatchFiles([]);
      setBatchProgress({ current: 0, total: 0, currentFile: '' });
      document.getElementById('batch-file-upload').value = '';
    }
    
    if (successCount > 0) {
      const msg = duplicateCount > 0 
        ? `Uploaded ${successCount} statements (${duplicateCount} duplicates replaced)`
        : `Uploaded ${successCount} statements`;
      toast.success(msg);
    }
    if (failedFilesList.length > 0) {
      toast.error(`Failed: ${failedFilesList.map(f => f.name).slice(0, 3).join(', ')}${failedFilesList.length > 3 ? '...' : ''}`);
    }
    
    fetchStatements();
  };

  const handleRetryBatchUpload = () => {
    if (pendingBatchFiles.length > 0) {
      handleBatchUpload(true);
    }
  };

  const handleDelete = async (statementId) => {
    if (!window.confirm('Delete this statement and all its transactions?')) return;

    try {
      await api.delete(`/statements/${statementId}`);
      toast.success('Statement deleted');
      fetchStatements();
    } catch (error) {
      toast.error('Failed to delete statement');
    }
  };

  const handleDownload = async (statement) => {
    try {
      const response = await api.get(`/statements/${statement.id}/download`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${statement.statement_name}_${statement.month}_${statement.year}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleToggleExclude = async (statement) => {
    try {
      const response = await api.put(`/statements/${statement.id}/exclude`);
      const action = response.data.excluded ? 'excluded from' : 'included in';
      toast.success(`Statement ${action} analysis`);
      fetchStatements();
    } catch (error) {
      toast.error('Failed to update statement');
    }
  };

  const handleReload = async (statementId) => {
    if (!window.confirm('Reload this statement? Transactions will be re-categorized with current keywords.')) return;

    // Set this statement to loading
    setStatementReloadStatus(prev => ({ ...prev, [statementId]: 'loading' }));
    
    try {
      const response = await api.post(`/statements/${statementId}/reload`, {}, { timeout: 120000 });
      setStatementReloadStatus(prev => ({ ...prev, [statementId]: 'success' }));
      toast.success(`Reloaded! ${response.data.transactions_extracted} transactions extracted.`);
      fetchStatements();
      // Clear status after delay
      setTimeout(() => {
        setStatementReloadStatus(prev => {
          const newStatus = { ...prev };
          delete newStatus[statementId];
          return newStatus;
        });
      }, 3000);
    } catch (error) {
      setStatementReloadStatus(prev => ({ ...prev, [statementId]: 'failed' }));
      toast.error('Failed to reload statement');
    }
  };

  const handleReloadAll = async (retryFailed = false) => {
    // Check max limit
    if (statements.length > MAX_BATCH_LIMIT) {
      toast.error(`Maximum ${MAX_BATCH_LIMIT} statements allowed for bulk reload. Please reload individually or delete some statements.`);
      return;
    }
    
    // Determine which statements to reload
    let statementsToReload;
    if (retryFailed) {
      // Only reload failed statements
      statementsToReload = statements.filter(stmt => statementReloadStatus[stmt.id] === 'failed');
    } else {
      if (!window.confirm(`Reload all ${statements.length} statements? This will re-extract and re-categorize all transactions.`)) return;
      statementsToReload = statements;
    }
    
    if (statementsToReload.length === 0) {
      toast.info('No statements to reload');
      return;
    }

    setReloadingAll(true);
    setHasReloadFailures(false);
    
    // Initialize status - keep existing success statuses if retrying
    const initialStatus = { ...statementReloadStatus };
    statementsToReload.forEach(stmt => {
      initialStatus[stmt.id] = 'pending';
    });
    setStatementReloadStatus(initialStatus);
    
    let totalReloaded = 0;
    let totalTransactions = 0;
    let totalFailed = 0;
    
    try {
      for (let i = 0; i < statementsToReload.length; i++) {
        const stmt = statementsToReload[i];
        
        // Set current statement to loading
        setStatementReloadStatus(prev => ({ ...prev, [stmt.id]: 'loading' }));
        
        // Small delay to ensure UI updates
        await new Promise(resolve => setTimeout(resolve, 50));
        
        try {
          const response = await api.post(`/statements/${stmt.id}/reload`, {}, { timeout: 120000 });
          
          // Mark as success
          setStatementReloadStatus(prev => ({ ...prev, [stmt.id]: 'success' }));
          totalReloaded++;
          totalTransactions += response.data.transactions_extracted || 0;
          
        } catch (error) {
          const detail = error.response?.data?.detail || error.response?.data?.error || '';
          
          // Mark as failed
          setStatementReloadStatus(prev => ({ ...prev, [stmt.id]: 'failed' }));
          totalFailed++;
          
          // Check for budget exceeded - stop processing
          if (detail.includes('Budget') || detail.includes('budget')) {
            toast.error('LLM budget exceeded. Add more balance and retry.');
            // Mark remaining as pending (not attempted)
            for (let j = i + 1; j < statementsToReload.length; j++) {
              setStatementReloadStatus(prev => ({ ...prev, [statementsToReload[j].id]: 'failed' }));
            }
            totalFailed += statementsToReload.length - i - 1;
            break;
          }
        }
      }
      
      // Check if there are any failures
      setHasReloadFailures(totalFailed > 0);
      
      if (totalFailed > 0) {
        toast.warning(`Reloaded ${totalReloaded} statements (${totalFailed} failed) with ${totalTransactions} transactions.`);
      } else {
        toast.success(`Reloaded ${totalReloaded} statements with ${totalTransactions} transactions.`);
        // Clear status after delay only if all succeeded
        setTimeout(() => setStatementReloadStatus({}), 3000);
      }
      
      fetchStatements();
      
    } catch (error) {
      console.error('Reload error:', error);
      toast.error('Failed to reload statements.');
    } finally {
      setReloadingAll(false);
    }
  };

  const handleRetryReload = () => {
    handleReloadAll(true);
  };

  // Count completed/failed for progress display
  const reloadStats = {
    completed: Object.values(statementReloadStatus).filter(s => s === 'success').length,
    failed: Object.values(statementReloadStatus).filter(s => s === 'failed').length,
    total: statements.length
  };

  const handleDeleteAll = async () => {
    if (!window.confirm(`DELETE ALL ${statements.length} statements and their transactions? This cannot be undone!`)) return;
    if (!window.confirm('Are you absolutely sure? All your data will be permanently deleted.')) return;

    setDeletingAll(true);
    try {
      const response = await api.delete('/statements');
      toast.success(`Deleted ${response.data.deleted_count} statements`);
      fetchStatements();
    } catch (error) {
      toast.error('Failed to delete statements');
    } finally {
      setDeletingAll(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="p-6 space-y-6"
      data-testid="upload-page"
    >
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">
          Upload Statement
        </h1>
        <p className="text-sm text-muted-foreground">
          Retained for {settings?.retention_months || 18} months • Max {settings?.max_statements || 75} statements
        </p>
        {statements.length >= (settings?.max_statements || 75) && (
          <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 text-sm text-destructive font-medium" data-testid="limit-reached-alert">
            ⚠️ Statement limit reached ({statements.length}/{settings?.max_statements || 75}). Delete older statements to upload new ones.
          </div>
        )}
        {statements.length >= (settings?.max_statements || 75) - 5 && statements.length < (settings?.max_statements || 75) && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-sm text-yellow-700 font-medium" data-testid="limit-warning-alert">
            ⚠️ Approaching limit: {statements.length}/{settings?.max_statements || 75} statements used.
          </div>
        )}
      </div>

      {/* User Guide */}
      <Card className="border-primary/20 bg-muted/50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Info size={20} weight="bold" className="text-primary" />
            User Guide
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div>
            <p className="font-semibold mb-1">Single Upload</p>
            <p className="text-muted-foreground">Enter card name, PDF password, and select the file. Statement month/year is auto-extracted.</p>
          </div>
          <div>
            <p className="font-semibold mb-1">Batch Upload</p>
            <p className="text-muted-foreground">Rename each file as <span className="font-mono text-xs bg-background px-1 rounded">cardname_password.pdf</span> then select all files together.</p>
          </div>
          <div>
            <p className="font-semibold mb-1">Reload / Delete</p>
            <p className="text-muted-foreground">Use reload to re-extract transactions after updating category keywords. Delete removes statement and all its transactions.</p>
          </div>
          <div>
            <p className="font-semibold mb-1">Dashboard</p>
            <p className="text-muted-foreground">View spending by category. Set a 7+ day date range to see weekly spending trend.</p>
          </div>
          <div>
            <p className="font-semibold mb-1">Settings</p>
            <p className="text-muted-foreground">Manage category keywords and set high-value transaction threshold. Rename or delete categories as needed.</p>
          </div>
        </CardContent>
      </Card>

      {/* Upload Form */}
      <Card data-testid="upload-form">
        <CardHeader>
          <CardTitle className="text-lg">Single Upload</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleUpload} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs">Card Name</Label>
              <Input
                value={statementName}
                onChange={(e) => setStatementName(e.target.value)}
                placeholder="e.g., ICICI Personal Card"
                data-testid="statement-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs">PDF Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="PDF password"
                data-testid="password-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs">PDF File</Label>
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="file-upload"
                  data-testid="file-input"
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer flex flex-col items-center gap-2"
                >
                  <UploadSimple size={32} className="text-muted-foreground" />
                  {file ? (
                    <p className="text-sm font-medium">{file.name}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground">Tap to select PDF</p>
                  )}
                </label>
              </div>
            </div>

            <Button
              type="submit"
              disabled={uploading || !statementName || !file || !password}
              className="w-full rounded-full"
              data-testid="upload-button"
            >
              {uploading ? 'Extracting...' : 'Upload Statement'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Batch Upload */}
      <Card data-testid="batch-upload-form">
        <CardHeader>
          <CardTitle className="text-lg">Batch Upload</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-3 bg-muted/50 rounded-lg text-sm">
            <p className="text-muted-foreground mb-1">File naming format:</p>
            <p className="font-bold text-blue-600">cardname_password.pdf</p>
            <p className="text-muted-foreground mt-2">Example:</p>
            <p className="font-bold text-blue-600">ICICI_mypass123.pdf</p>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Select Multiple PDFs</Label>
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".pdf"
                multiple
                onChange={handleBatchFileSelect}
                className="hidden"
                id="batch-file-upload"
                data-testid="batch-file-input"
              />
              <label
                htmlFor="batch-file-upload"
                className="cursor-pointer flex flex-col items-center gap-2"
              >
                <UploadSimple size={32} className="text-muted-foreground" />
                {batchFiles.length > 0 ? (
                  <p className="text-sm font-medium">{batchFiles.length} files selected</p>
                ) : (
                  <p className="text-sm text-muted-foreground">Tap to select multiple PDFs</p>
                )}
              </label>
            </div>
          </div>

          {/* Batch Upload Progress Bar */}
          {(batchUploading || batchProgress.total > 0) && (
            <div className="space-y-2" data-testid="batch-progress-container">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span className="truncate flex-1 mr-2">
                  {batchProgress.currentFile ? `Uploading: ${batchProgress.currentFile}` : 'Uploading...'}
                </span>
                <span className="flex-shrink-0">{batchProgress.current}/{batchProgress.total}</span>
              </div>
              <Progress value={(batchProgress.current / batchProgress.total) * 100} className="h-2 bg-muted [&>div]:bg-blue-500" />
            </div>
          )}

          {/* Show Retry button when there are failed files */}
          {batchFailedFiles.length > 0 && !batchUploading ? (
            <Button
              onClick={handleRetryBatchUpload}
              disabled={batchUploading}
              className="w-full rounded-full bg-blue-500 hover:bg-blue-600 text-white"
              data-testid="batch-retry-button"
            >
              <ArrowClockwise size={18} className="mr-2" />
              Retry Upload ({batchFailedFiles.length} failed)
            </Button>
          ) : (
            <Button
              onClick={() => handleBatchUpload(false)}
              disabled={batchUploading || batchFiles.length === 0}
              className="w-full rounded-full"
              data-testid="batch-upload-button"
            >
              {batchUploading 
                ? `Uploading ${batchProgress.current}/${batchProgress.total}...` 
                : `Upload ${batchFiles.length || ''} Statements`}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Statement List */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold">Statements ({statements.length})</h2>
        {statements.length === 0 ? (
          <Card className="text-center p-8" data-testid="no-statements">
            <p className="text-muted-foreground">No statements uploaded yet</p>
          </Card>
        ) : (
          <>
            <div className="space-y-3">
              {statements.map((stmt, index) => {
                const status = statementReloadStatus[stmt.id];
                const isExcluded = stmt.excluded;
                return (
                  <Card key={stmt.id} data-testid="statement-card" className={`
                    ${isExcluded ? 'border-[#800000] bg-[#800000]/10' : ''}
                    ${status === 'loading' && !isExcluded ? 'border-blue-500 bg-blue-50/50' : ''}
                    ${status === 'success' && !isExcluded ? 'border-green-500 bg-green-50/50' : ''}
                    ${status === 'failed' && !isExcluded ? 'border-red-500 bg-red-50/50' : ''}
                  `}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      {/* Status indicator */}
                      {status === 'loading' && (
                        <CircleNotch size={20} className="text-blue-500 animate-spin flex-shrink-0" />
                      )}
                      {status === 'success' && (
                        <CheckCircle size={20} className="text-green-500 flex-shrink-0" weight="fill" />
                      )}
                      {status === 'failed' && (
                        <XCircle size={20} className="text-red-500 flex-shrink-0" weight="fill" />
                      )}
                      {status === 'pending' && (
                        <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30 flex-shrink-0" />
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <p className={`font-semibold truncate ${isExcluded ? 'text-[#800000]' : ''}`}>
                          {stmt.statement_name}
                          {isExcluded && <span className="ml-2 text-xs font-normal">(Excluded)</span>}
                        </p>
                        <p className={`text-sm ${isExcluded ? 'text-[#800000]/70' : 'text-muted-foreground'}`}>
                          {stmt.month}/{stmt.year} • {stmt.transaction_count} transactions
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleExclude(stmt)}
                        disabled={reloadingAll}
                        data-testid="exclude-statement-button"
                        title={isExcluded ? "Include in analysis" : "Exclude from analysis"}
                      >
                        <Prohibit size={20} className={isExcluded ? 'text-[#800000]' : 'text-muted-foreground'} weight={isExcluded ? 'fill' : 'regular'} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownload(stmt)}
                        disabled={reloadingAll}
                        data-testid="download-statement-button"
                        title="Download PDF"
                      >
                        <DownloadSimple size={20} className="text-blue-500" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleReload(stmt.id)}
                        disabled={reloadingAll || status === 'loading'}
                        data-testid="reload-statement-button"
                        title="Reload"
                      >
                        <ArrowsClockwise size={20} className={`text-primary ${status === 'loading' ? 'animate-spin' : ''}`} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(stmt.id)}
                        disabled={reloadingAll}
                        data-testid="delete-statement-button"
                        title="Delete"
                      >
                        <Trash size={20} className="text-destructive" />
                      </Button>
                    </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          
            {/* Bulk Actions */}
            <div className="space-y-3 pt-2">
              {/* Reload Status Summary */}
              {(reloadingAll || Object.keys(statementReloadStatus).length > 0) && (
                <div className="text-xs text-center text-muted-foreground" data-testid="reload-status">
                  {reloadingAll ? 'Reloading... ' : ''}{reloadStats.completed}/{reloadStats.total} complete
                  {reloadStats.failed > 0 && ` (${reloadStats.failed} failed)`}
                </div>
              )}
              
              <div className="flex gap-2">
                {/* Show Retry button when there are failures and not currently reloading */}
                {hasReloadFailures && !reloadingAll ? (
                  <Button
                    onClick={handleRetryReload}
                    disabled={reloadingAll}
                    className="flex-1 rounded-full bg-blue-500 hover:bg-blue-600 text-white"
                    data-testid="reload-retry-button"
                  >
                    <ArrowClockwise size={18} className="mr-2" />
                    Retry Failed ({reloadStats.failed})
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    className="flex-1 rounded-full"
                    onClick={() => handleReloadAll(false)}
                    disabled={reloadingAll || statements.length === 0}
                    data-testid="reload-all-button"
                  >
                    <ArrowsClockwise size={18} className={`mr-2 ${reloadingAll ? 'animate-spin' : ''}`} />
                    {reloadingAll ? `Reloading ${reloadStats.completed}/${reloadStats.total}...` : 'Reload All'}
                  </Button>
                )}
                
                <Button
                  variant="outline"
                  className="flex-1 rounded-full border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                  onClick={handleDeleteAll}
                  disabled={deletingAll || reloadingAll || statements.length === 0}
                  data-testid="delete-all-button"
                >
                  <Trash size={18} className="mr-2" />
                  {deletingAll ? 'Deleting...' : 'Delete All'}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
};

export default Upload;
